/**
 * Canvas world map: Natural Earth layers, graticule, scale bar, city labels with collision,
 * pins/lines overlay, hit-testing and d3-zoom pan/zoom. Port of the prototype renderer.
 */
import { zoom as d3zoom, zoomIdentity, type ZoomBehavior, type ZoomTransform } from "d3-zoom";
import { select, type Selection } from "d3-selection";
import "d3-transition";
import { easeCubicInOut } from "d3-ease";
import { decodeTopo, type Layers, type Topology } from "./topo";
import { WU, X, Y, latOfY, lngOfX, spanPoints, unwrapLine, wrapLng } from "./projection";
import { readColors, onThemeChange, type Colors } from "../ui/theme";
import { reducedMotion } from "../ui/dom";
import type { LatLng } from "../game/geo";
import { t as strings } from "../strings";
import { bestOn, coastColor, contextWorldPx, distToBox, fanOut, fitPadding, lineBoxes, LEADER_GAP, nearestOnBox, placeLabels, placeLineLabel, ringRadiusPx, RING_LABEL_MIN_PX, RING_MIN_PX, type Box } from "./layout";

export type PinKind = "fill" | "ring" | "guess" | "place";

export interface Pin extends LatLng {
  kind?: PinKind;
  color: string;
  /** Number or letter drawn inside the pin. */
  text?: string | null;
  label?: string | null;
  sub?: string | null;
  /** Draw order: higher on top, and labelled first. */
  z?: number;
  r?: number;
  halo?: boolean;
  /** Label even if it overlaps. */
  force?: boolean;
  /** Clickable pin id (onPinClick). */
  id?: number;
  /** What the pin stands for ("guess", "answer", "trail"…); listed in the canvas's data-pins for tests. */
  tag?: string;
}

export interface Line {
  /** [lat, lng] pairs */
  pts: [number, number][];
  color: string;
  width?: number;
  dash?: boolean;
  label?: string;
  /** What the line stands for ("distance", "route"…); listed in the canvas's data-lines for tests. */
  tag?: string;
}

/** Small popup anchored to a map point, used after a round to show what happened there. */
export interface Callout extends LatLng {
  title: string;
  sub?: string | null;
  body?: string | null;
}

/** Distance ring around a point (Pin it answer): radius in km on the ground, label drawn at its top. */
export interface Ring extends LatLng {
  km: number;
  label: string;
  color: string;
}

export interface Overlay {
  pins?: Pin[];
  lines?: Line[];
  rings?: Ring[];
  cities?: boolean;
}

interface Place {
  name: string;
  sr: number;
  cap: number;
  x: number;
  y: number;
}

const DATA = `${import.meta.env.BASE_URL}data/`;
const DETAIL_AT = 30000; // world width in px above which 10m detail is used
const PAN_STEP = 60; // keyboard pan, px (Shift: ×4)

/** Accessible names for the focusable map; en.ts may override them with `ui.mapKeys` / `ui.mapKeysPick`. */
const KEY_HELP = {
  view: "Map. Arrow keys move the map, plus and minus zoom.",
  pick: "Map. Arrow keys move the map, plus and minus zoom, Enter drops a pin at the centre.",
};
const keyHelp = (pick: boolean): string => {
  const ui = strings.ui as { mapKeys?: string; mapKeysPick?: string };
  return (pick ? ui.mapKeysPick : ui.mapKeys) ?? KEY_HELP[pick ? "pick" : "view"];
};

/** Tokens the canvas reads on top of readColors(); empty string when a theme doesn't define them. */
interface Extra {
  coast: string;
  onGood: string;
  onBad: string;
  onAccent: string;
  focus: string;
}
function readExtra(): Extra {
  const cs = getComputedStyle(document.documentElement);
  const v = (k: string) => cs.getPropertyValue(`--${k}`).trim();
  return { coast: v("coast"), onGood: v("on-good"), onBad: v("on-bad"), onAccent: v("accent-ink"), focus: v("focus") };
}

export class MapView {
  readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly wrap: HTMLElement;
  private readonly zoom: ZoomBehavior<HTMLCanvasElement, unknown>;
  private readonly sel: Selection<HTMLCanvasElement, unknown, null, undefined>;
  private C: Colors;
  private E: Extra;
  private picking = false;
  private kbFocus = false;
  private W = 0;
  private H = 0;
  private dpr = 1;
  private t: ZoomTransform = zoomIdentity;
  private base: Layers = {};
  private detail: Layers = {};
  private detailLoading = false;
  private places: Place[] = [];
  private overlay: Required<Overlay> = { pins: [], lines: [], rings: [], cities: true };
  private hits: { id: number; sx: number; sy: number; r: number }[] = [];
  private rq = false;
  private callout: Callout | null = null;
  private readonly calloutEl: HTMLDivElement;

  onMapClick: ((p: LatLng) => void) | null = null;
  onPinClick: ((id: number) => void) | null = null;
  /** Re-frames the view for the current round; also used by the reset button. */
  homeView: ((animate: boolean) => void) | null = null;

  constructor(wrap: HTMLElement, canvas: HTMLCanvasElement) {
    this.wrap = wrap;
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d")!;
    this.C = readColors();
    this.E = readExtra();
    this.zoom = d3zoom<HTMLCanvasElement, unknown>()
      .translateExtent([[-1e8, 0], [1e8, WU]])
      .on("zoom", (e: { transform: ZoomTransform }) => {
        this.t = e.transform;
        this.requestRender();
      });
    this.calloutEl = document.createElement("div");
    this.calloutEl.className = "callout";
    this.calloutEl.hidden = true;
    this.calloutEl.setAttribute("role", "dialog");
    wrap.append(this.calloutEl);
    this.sel = select(canvas);
    this.sel.call(this.zoom).on("dblclick.zoom", null);

    new ResizeObserver(() => {
      const first = this.W === 0;
      this.resize();
      if (first) this.homeView?.(false);
    }).observe(wrap);
    this.resize();
    // Start from the world view so the first round's fly-in doesn't begin at the zoom identity (a close-up of Arctic sea).
    this.worldView(0);

    // keyboard: the canvas is focusable; arrows pan, +/- zoom, Enter/Space drops a pin at the centre crosshair
    canvas.tabIndex = 0;
    canvas.setAttribute("role", "application");
    canvas.setAttribute("aria-label", keyHelp(false));
    canvas.style.outlineOffset = "-3px"; // the wrapper clips overflow, so draw the focus ring inside the canvas
    canvas.addEventListener("focus", () => {
      this.kbFocus = canvas.matches(":focus-visible");
      this.requestRender();
    });
    canvas.addEventListener("blur", () => {
      this.kbFocus = false;
      this.requestRender();
    });
    canvas.addEventListener("pointerdown", () => {
      if (this.kbFocus) {
        this.kbFocus = false;
        this.requestRender();
      }
    });
    canvas.addEventListener("keydown", (e) => this.onKey(e));

    canvas.addEventListener("mousemove", (e) => {
      const r = canvas.getBoundingClientRect();
      canvas.classList.toggle("hot", !!(this.onPinClick && this.hitAt(e.clientX - r.left, e.clientY - r.top)));
    });
    canvas.addEventListener("click", (e) => {
      const r = canvas.getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      const hit = this.hitAt(x, y);
      if (hit && this.onPinClick) return this.onPinClick(hit.id);
      if (this.callout) this.showCallout(null);
      if (this.onMapClick) this.onMapClick(this.toLatLng(x, y));
    });
    onThemeChange(() => {
      this.C = readColors();
      this.E = readExtra();
      this.requestRender();
    });
    void this.loadBase();
  }

  /** Current theme colours, for pins and lines. */
  get colors(): Colors {
    return this.C;
  }

  get width(): number {
    return this.W;
  }

  /** Still about as far out as the whole world: the world is at most three views wide. */
  get worldScale(): boolean {
    return WU * this.t.k <= 3 * this.W;
  }

  toLatLng(x: number, y: number): LatLng {
    return { lat: latOfY((y - this.t.y) / this.t.k), lng: wrapLng(lngOfX((x - this.t.x) / this.t.k)) };
  }

  /** Screen position of a point (first world copy), for tests and tooling. */
  toScreen(p: LatLng): { x: number; y: number } {
    return { x: this.t.x + X(p.lng) * this.t.k, y: this.t.y + Y(p.lat) * this.t.k };
  }

  set(o: Overlay): void {
    this.overlay = { pins: [], lines: [], rings: [], cities: true, ...o };
    // What is on the map, for tests: tagged pins and lines in draw order, and where the player's pin is.
    const d = this.canvas.dataset;
    d.pins = this.overlay.pins.flatMap((p) => (p.tag ? [p.tag] : [])).join(",");
    d.lines = this.overlay.lines.flatMap((l) => (l.tag ? [l.tag] : [])).join(",");
    const g = this.overlay.pins.find((p) => p.kind === "guess");
    if (g) d.guess = `${g.lat.toFixed(5)},${g.lng.toFixed(5)}`;
    else delete d.guess;
    this.requestRender();
  }

  /** Show (or with null, hide) the popup for a point. Cleared by the next map click. */
  showCallout(c: Callout | null): void {
    this.callout = c;
    const el = this.calloutEl;
    el.hidden = !c;
    if (c) {
      el.replaceChildren();
      const b = document.createElement("b");
      b.textContent = c.title;
      el.append(b);
      if (c.sub) {
        const s = document.createElement("small");
        s.textContent = c.sub;
        el.append(s);
      }
      if (c.body) {
        const p = document.createElement("p");
        p.textContent = c.body;
        el.append(p);
      }
    }
    this.requestRender();
  }

  private placeCallout(): void {
    const c = this.callout;
    if (!c) return;
    const el = this.calloutEl;
    // nearest world copy to the centre of the view
    const wpx = WU * this.t.k;
    let x = this.t.x + X(c.lng) * this.t.k;
    x += Math.round((this.W / 2 - x) / wpx) * wpx;
    const y = this.t.y + Y(c.lat) * this.t.k;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const left = Math.max(8, Math.min(this.W - w - 8, x - w / 2));
    const above = y - h - 16;
    const top = above >= 8 ? above : Math.min(this.H - h - 8, y + 16);
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  }

  setPicking(on: boolean): void {
    this.canvas.classList.toggle("pick", on);
    if (on !== this.picking) {
      this.picking = on;
      this.canvas.setAttribute("aria-label", keyHelp(on));
      this.requestRender();
    }
  }

  private onKey(e: KeyboardEvent): void {
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    const step = PAN_STEP * (e.shiftKey ? 4 : 1);
    const pan = (dx: number, dy: number) => this.sel.interrupt().call(this.zoom.translateBy, dx / this.t.k, dy / this.t.k);
    let handled = true;
    switch (e.key) {
      case "ArrowLeft":
        pan(step, 0);
        break;
      case "ArrowRight":
        pan(-step, 0);
        break;
      case "ArrowUp":
        pan(0, step);
        break;
      case "ArrowDown":
        pan(0, -step);
        break;
      case "+":
      case "=":
        this.zoomBy(2);
        break;
      case "-":
      case "_":
        this.zoomBy(0.5);
        break;
      case "Enter":
      case " ":
        if (this.picking && this.onMapClick) {
          if (this.callout) this.showCallout(null);
          this.onMapClick(this.toLatLng(this.W / 2, this.H / 2));
        } else handled = false;
        break;
      case "Escape":
        if (this.callout) this.showCallout(null);
        else handled = false;
        break;
      default:
        handled = false;
    }
    if (handled) {
      e.preventDefault();
      // keyboard use: show the crosshair even if focus came from a click
      if (!this.kbFocus) {
        this.kbFocus = true;
        this.requestRender();
      }
    }
  }

  zoomBy(f: number): void {
    this.sel.transition().duration(reducedMotion() ? 0 : 250).call(this.zoom.scaleBy, f);
  }

  // ---------- data ----------
  private async loadBase(): Promise<void> {
    try {
      this.base = decodeTopo((await (await fetch(`${DATA}base.json`)).json()) as Topology);
    } catch {
      /* map stays blank; the game still works from the lists */
    }
    this.requestRender();
    try {
      const arr = (await (await fetch(`${DATA}places.json`)).json()) as [string, number, number, number, number][];
      this.places = arr.map(([name, lng, lat, sr, cap]) => ({ name, sr, cap, x: X(lng), y: Y(lat) })).sort((a, b) => a.sr - b.sr);
      this.requestRender();
    } catch {
      /* no city labels */
    }
  }

  private async loadDetail(): Promise<void> {
    if (this.detailLoading) return;
    this.detailLoading = true;
    try {
      this.detail = decodeTopo((await (await fetch(`${DATA}detail.json`)).json()) as Topology);
      this.requestRender();
    } catch {
      /* keep the 50m layers */
    }
  }

  // ---------- view ----------
  private minK(): number {
    return Math.max(this.W, 320) / WU;
  }

  private resize(): void {
    const r = this.wrap.getBoundingClientRect();
    this.W = Math.max(1, r.width);
    this.H = Math.max(1, r.height);
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.canvas.width = Math.round(this.W * this.dpr);
    this.canvas.height = Math.round(this.H * this.dpr);
    this.zoom.scaleExtent([this.minK(), 3e6 / WU]).extent([[0, 0], [this.W, this.H]]);
    this.requestRender();
  }

  private goTo(tr: ZoomTransform, ms: number): void {
    if (ms && !reducedMotion()) this.sel.transition().duration(ms).ease(easeCubicInOut).call(this.zoom.transform, tr);
    else this.sel.interrupt().call(this.zoom.transform, tr);
  }

  fitPoints(pts: LatLng[], { ms = 800, maxWorldPx = 250000, minWorldPx = 0, padBottom = 0 } = {}): void {
    if (this.W <= 1) return;
    if (!pts.length) return this.worldView(ms);
    // frame the shortest arc that holds every place (cut at the widest empty gap), whatever order they come in
    const u = spanPoints(pts.map((p) => [p.lat, p.lng] as [number, number]));
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    for (const [x, y] of u) {
      x0 = Math.min(x0, x);
      x1 = Math.max(x1, x);
      y0 = Math.min(y0, y);
      y1 = Math.max(y1, y);
    }
    const pad = fitPadding(this.W);
    // keep pins clear of the zoom buttons, whatever size the stylesheet gives them
    pad.r = Math.max(pad.r, this.W - this.controlsBox()[0] + 14);
    pad.b += padBottom; // e.g. the score screen's legend
    const iw = Math.max(40, this.W - pad.l - pad.r);
    const ih = Math.max(40, this.H - pad.t - pad.b);
    // one place (or a tight cluster): keep a few hundred km of context rather than a street-level close-up
    const cap = contextWorldPx(this.W, this.H, latOfY((y0 + y1) / 2));
    let k = Math.min(iw / Math.max(x1 - x0, 1e-9), ih / Math.max(y1 - y0, 1e-9));
    k = Math.min(k, maxWorldPx / WU, cap / WU);
    k = Math.max(k, Math.min(minWorldPx, cap) / WU, this.minK());
    if (k * WU > DETAIL_AT) void this.loadDetail();
    const cx = pad.l + iw / 2;
    const cy = pad.t + ih / 2;
    this.goTo(zoomIdentity.translate(cx - ((x0 + x1) / 2) * k, cy - ((y0 + y1) / 2) * k).scale(k), ms);
  }

  worldView(ms = 600): void {
    const k = this.minK();
    this.goTo(zoomIdentity.translate(this.W / 2 - X(10) * k, this.H / 2 - Y(25) * k).scale(k), ms);
  }

  // ---------- rendering ----------
  requestRender(): void {
    if (this.rq) return;
    this.rq = true;
    requestAnimationFrame(() => {
      this.rq = false;
      this.render();
    });
  }

  private offsets(): number[] {
    const wpx = WU * this.t.k;
    const out: number[] = [];
    const n0 = Math.floor(-this.t.x / wpx) - 1;
    const n1 = Math.ceil((this.W - this.t.x) / wpx);
    for (let n = n0; n <= n1; n++) {
      const x0 = this.t.x + n * wpx;
      if (x0 + wpx >= -wpx * 0.5 && x0 <= this.W + wpx * 0.5) out.push(n);
    }
    return out.slice(0, 6);
  }

  private haloText(s: string, x: number, y: number, color: string, lw = 3.5): void {
    const ctx = this.ctx;
    ctx.lineJoin = "round";
    ctx.strokeStyle = this.C.halo;
    ctx.lineWidth = lw;
    ctx.strokeText(s, x, y);
    ctx.fillStyle = color;
    ctx.fillText(s, x, y);
  }

  private render(): void {
    if (this.W <= 1) return;
    const { ctx, C, t, dpr } = this;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = C.sea;
    ctx.fillRect(0, 0, this.W, this.H);
    const k = t.k;
    const wpx = WU * k;
    const offs = this.offsets();
    const useDetail = wpx > DETAIL_AT;
    if (useDetail && !this.detail.land) void this.loadDetail();
    const L = useDetail && this.detail.land ? this.detail : this.base;
    const coast = coastColor(this.E.coast, C["land-edge"], C.sea, C.land, C.ink);
    for (const n of offs) {
      ctx.setTransform(dpr * k, 0, 0, dpr * k, dpr * (t.x + n * wpx), dpr * t.y);
      if (L.land) {
        ctx.fillStyle = C.land;
        ctx.fill(L.land, "evenodd");
      }
      if (this.base.lakes) {
        ctx.fillStyle = C.lake;
        ctx.fill(this.base.lakes, "evenodd");
      }
      if (L.land) {
        ctx.strokeStyle = coast;
        ctx.lineWidth = (coast === C["land-edge"] ? 0.8 : 1.1) / k;
        ctx.stroke(L.land);
      }
      if (this.base.states && wpx > 5000) {
        ctx.strokeStyle = C.state;
        ctx.lineWidth = 0.7 / k;
        ctx.stroke(this.base.states);
      }
      if (L.borders) {
        ctx.strokeStyle = C["border-line"];
        ctx.lineWidth = 0.9 / k;
        ctx.setLineDash([4 / k, 3 / k]);
        ctx.stroke(L.borders);
        ctx.setLineDash([]);
      }
      const rv = useDetail && this.detail.rivers ? this.detail.rivers : this.base.rivers;
      if (rv && wpx > 1400) {
        ctx.strokeStyle = C.river;
        ctx.lineWidth = (wpx > DETAIL_AT ? 1.3 : 0.9) / k;
        ctx.stroke(rv);
      }
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.drawGraticule(k);
    // the zoom buttons (top right) and the scale bar (bottom right) are kept free of labels
    const occ: Box[] = [this.controlsBox(), [this.W - 140, this.H - 40, this.W, this.H]];
    this.hits = [];
    this.drawRings(offs, wpx);
    this.drawOverlay(offs, wpx, occ);
    if (this.overlay.cities) this.drawCities(offs, wpx, occ);
    this.drawScale(wpx);
    if (this.kbFocus && this.picking) this.drawCrosshair();
    this.placeCallout();
  }

  private drawGraticule(k: number): void {
    const { ctx, C, t, W, H } = this;
    const niceStep = (span: number) => {
      for (const s of [60, 30, 15, 10, 5, 2, 1, 0.5, 0.25, 0.1, 0.05, 0.02]) if (span / s >= 3.5) return s;
      return 0.01;
    };
    const lng0 = lngOfX(-t.x / k);
    const lng1 = lngOfX((W - t.x) / k);
    const lat1 = latOfY(-t.y / k);
    const lat0 = latOfY((H - t.y) / k);
    const step = niceStep(Math.min(lng1 - lng0, 360));
    const dec = step < 1 ? (step < 0.1 ? 2 : 1) : 0;
    ctx.strokeStyle = C.grat;
    ctx.lineWidth = 1;
    ctx.font = `10px ${C.fmono}`;
    ctx.beginPath();
    const labels: ["x" | "y", number, string][] = [];
    for (let g = Math.ceil(lng0 / step) * step; g <= lng1; g += step) {
      const sx = Math.round(t.x + X(g) * k) + 0.5;
      ctx.moveTo(sx, 0);
      ctx.lineTo(sx, H);
      const w = wrapLng(g);
      labels.push(["x", sx, `${Math.abs(w).toFixed(dec)}°${w === 0 || Math.abs(w) === 180 ? "" : w > 0 ? "E" : "W"}`]);
    }
    const ls = niceStep(Math.max(0.01, lat1 - lat0));
    for (let g = Math.ceil(Math.max(-80, lat0) / ls) * ls; g <= Math.min(80, lat1); g += ls) {
      const sy = Math.round(t.y + Y(g) * k) + 0.5;
      ctx.moveTo(0, sy);
      ctx.lineTo(W, sy);
      labels.push(["y", sy, `${Math.abs(g).toFixed(ls < 1 ? (ls < 0.1 ? 2 : 1) : 0)}°${g === 0 ? "" : g > 0 ? "N" : "S"}`]);
    }
    ctx.stroke();
    ctx.fillStyle = C["grat-text"];
    ctx.textBaseline = "top";
    for (const [a, v, s] of labels) {
      if (a === "x") ctx.fillText(s, v + 3, 4);
      else ctx.fillText(s, 4, v + 3);
    }
  }

  /** Where the zoom buttons sit over the map (top right), in canvas px. */
  private controlsBox(): Box {
    const el = this.wrap.querySelector(".ctrls");
    if (el) {
      const w = this.wrap.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      if (r.width > 0) return [r.left - w.left - 4, 0, this.W, r.bottom - w.top + 4];
    }
    return [this.W - 52, 0, this.W, 136];
  }

  /** Keyboard picking: where Enter drops the pin. */
  private drawCrosshair(): void {
    const { ctx, C } = this;
    const x = Math.round(this.W / 2) + 0.5;
    const y = Math.round(this.H / 2) + 0.5;
    const col = this.E.focus || C.guess;
    const seg = (w: number, color: string) => {
      ctx.strokeStyle = color;
      ctx.lineWidth = w;
      ctx.beginPath();
      ctx.arc(x, y, 11, 0, 7);
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
        ctx.moveTo(x + dx * 5, y + dy * 5);
        ctx.lineTo(x + dx * 20, y + dy * 20);
      }
      ctx.stroke();
    };
    ctx.save();
    ctx.lineCap = "round";
    seg(5, C.halo);
    seg(2, col);
    ctx.beginPath();
    ctx.arc(x, y, 2, 0, 7);
    ctx.fillStyle = col;
    ctx.fill();
    ctx.restore();
  }

  private drawScale(wpx: number): void {
    const { ctx, C, t, W, H } = this;
    const lat = latOfY((H / 2 - t.y) / t.k);
    const mpp = (2 * Math.PI * 6371000 * Math.cos((lat * Math.PI) / 180)) / wpx;
    const target = mpp * 110;
    const p = Math.pow(10, Math.floor(Math.log10(target)));
    const nice = [1, 2, 5, 10].map((m) => m * p).filter((v) => v <= target).pop() ?? p;
    const px = nice / mpp;
    const x0 = W - 20 - px;
    const y0 = H - 22;
    ctx.strokeStyle = C.ink;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x0, y0 - 5);
    ctx.lineTo(x0, y0);
    ctx.lineTo(x0 + px, y0);
    ctx.lineTo(x0 + px, y0 - 5);
    ctx.stroke();
    ctx.font = `10px ${C.fmono}`;
    ctx.textBaseline = "bottom";
    ctx.textAlign = "right";
    this.haloText(nice >= 1000 ? `${nice / 1000} km` : `${nice} m`, x0 + px, y0 - 7, C.ink);
    ctx.textAlign = "left";
  }

  /** Text colour for a number/letter drawn on a filled pin: theme tokens for good/bad/accent, else best contrast. */
  private onFill(fill: string): string {
    const { C, E } = this;
    const auto = bestOn(fill, [C.panel, C.ink, "#ffffff"]);
    if (fill === C.good) return E.onGood || auto;
    if (fill === C.bad) return E.onBad || auto;
    if (fill === C.accent) return E.onAccent || auto;
    return auto;
  }

  /** Distance rings, under lines and pins. Rings too small to read at this zoom are skipped; the drawn radii go to data-rings (e2e). */
  private drawRings(offs: number[], wpx: number): void {
    const { ctx, t } = this;
    const drawn: number[] = [];
    for (const r of this.overlay.rings) {
      const rad = ringRadiusPx(r.km, r.lat, wpx);
      if (rad < RING_MIN_PX) continue;
      drawn.push(r.km);
      for (const n of offs) {
        const cx = t.x + n * wpx + X(r.lng) * t.k;
        const cy = t.y + Y(r.lat) * t.k;
        ctx.beginPath();
        ctx.arc(cx, cy, rad, 0, Math.PI * 2);
        ctx.strokeStyle = this.C.halo;
        ctx.lineWidth = 4;
        ctx.stroke();
        ctx.strokeStyle = r.color;
        ctx.lineWidth = 1.6;
        ctx.setLineDash([4, 4]);
        ctx.stroke();
        ctx.setLineDash([]);
        if (rad >= RING_LABEL_MIN_PX) {
          ctx.font = `600 11px ${this.C.fmono}`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          this.haloText(r.label, cx, cy - rad, r.color);
        }
      }
    }
    this.canvas.dataset.rings = drawn.join(",");
  }

  private drawOverlay(offs: number[], wpx: number, occ: Box[]): void {
    const { ctx, C, t, W, H } = this;
    const k = t.k;
    const bounds: Box = [2, 2, W - 2, H - 2];
    const lineLabels: { label: string; a: { x: number; y: number }; b: { x: number; y: number } }[] = [];
    const soft: Box[] = []; // route lines: pin labels keep off them where they can
    for (const ln of this.overlay.lines) {
      const pts = unwrapLine(ln.pts);
      for (const n of offs) {
        const ox = t.x + n * wpx;
        soft.push(...lineBoxes(pts.map((p) => ({ x: ox + p[0] * k, y: t.y + p[1] * k })), bounds));
        ctx.beginPath();
        pts.forEach((p, i) => {
          const sx = ox + p[0] * k;
          const sy = t.y + p[1] * k;
          if (i) ctx.lineTo(sx, sy);
          else ctx.moveTo(sx, sy);
        });
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        ctx.strokeStyle = C.halo;
        ctx.lineWidth = (ln.width ?? 2.6) + 3.5;
        ctx.setLineDash([]);
        ctx.stroke();
        ctx.strokeStyle = ln.color;
        ctx.lineWidth = ln.width ?? 2.6;
        ctx.setLineDash(ln.dash ? [7, 6] : []);
        ctx.stroke();
        ctx.setLineDash([]);
        if (ln.label && pts.length > 1) {
          const a = pts[0]!;
          const b = pts[pts.length - 1]!;
          const sa = { x: ox + a[0] * k, y: t.y + a[1] * k };
          const sb = { x: ox + b[0] * k, y: t.y + b[1] * k };
          const mx = (sa.x + sb.x) / 2;
          const my = (sa.y + sb.y) / 2;
          if (mx > -80 && mx < W + 80 && my > -40 && my < H + 40) lineLabels.push({ label: ln.label, a: sa, b: sb });
        }
      }
    }

    // pins: one copy each, the world copy nearest the middle of the view (when the whole world fits on screen, a pin
    // near one edge would otherwise show again at the other, with its label), fanned out where they'd cover each other
    const marks: { p: Pin; x0: number; y0: number; sx: number; sy: number; r: number; moved: boolean }[] = [];
    for (const p of this.overlay.pins) {
      const r = p.kind === "guess" ? 9 : p.kind === "place" ? 8 : (p.r ?? (p.text ? 10 : 7));
      let sx = t.x + X(p.lng) * k;
      sx += Math.round((W / 2 - sx) / wpx) * wpx;
      const sy = t.y + Y(p.lat) * k;
      if (sx < -60 || sx > W + 60 || sy < -60 || sy > H + 60) continue;
      marks.push({ p, x0: sx, y0: sy, sx, sy, r, moved: false });
    }
    fanOut(marks.map((m) => ({ x: m.x0, y: m.y0, r: m.r }))).forEach((f, i) => {
      const m = marks[i]!;
      m.sx = f.x;
      m.sy = f.y;
      m.moved = f.moved;
    });
    // leader lines from a displaced pin back to its real spot
    ctx.save();
    for (const m of marks) {
      if (!m.moved) continue;
      ctx.beginPath();
      ctx.moveTo(m.x0, m.y0);
      ctx.lineTo(m.sx, m.sy);
      ctx.strokeStyle = C.halo;
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.strokeStyle = C["ink-2"];
      ctx.lineWidth = 1;
      ctx.stroke();
    }
    for (const m of marks) {
      if (!m.moved) continue;
      ctx.beginPath();
      ctx.arc(m.x0, m.y0, 2.6, 0, 7);
      ctx.fillStyle = C["ink-2"];
      ctx.fill();
      ctx.lineWidth = 1.2;
      ctx.strokeStyle = C.halo;
      ctx.stroke();
    }
    ctx.restore();

    marks.sort((a, b) => (a.p.z ?? 0) - (b.p.z ?? 0));
    for (const { p, sx, sy, r } of marks) {
      const col = p.color || C.accent;
      ctx.save();
      if (p.kind === "guess") {
        ctx.beginPath();
        ctx.arc(sx, sy, 9, 0, 7);
        ctx.fillStyle = C.halo;
        ctx.fill();
        ctx.lineWidth = 3;
        ctx.strokeStyle = col;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(sx, sy, 3, 0, 7);
        ctx.fillStyle = col;
        ctx.fill();
      } else if (p.kind === "ring") {
        ctx.beginPath();
        ctx.arc(sx, sy, r, 0, 7);
        ctx.fillStyle = C.panel;
        ctx.fill();
        ctx.lineWidth = 2.2;
        ctx.strokeStyle = col;
        ctx.stroke();
      } else if (p.kind === "place") {
        ctx.beginPath();
        ctx.arc(sx, sy, 16, 0, 7);
        ctx.fillStyle = col;
        ctx.globalAlpha = 0.18;
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.beginPath();
        ctx.arc(sx, sy, 8, 0, 7);
        ctx.fillStyle = col;
        ctx.fill();
        ctx.lineWidth = 2.5;
        ctx.strokeStyle = C.panel;
        ctx.stroke();
      } else {
        if (p.halo) {
          ctx.beginPath();
          ctx.arc(sx, sy, r + 6, 0, 7);
          ctx.fillStyle = col;
          ctx.globalAlpha = 0.2;
          ctx.fill();
          ctx.globalAlpha = 1;
        }
        ctx.beginPath();
        ctx.arc(sx, sy, r, 0, 7);
        ctx.fillStyle = col;
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.strokeStyle = C.panel;
        ctx.stroke();
      }
      ctx.restore();
      if (p.text) {
        ctx.font = `600 10.5px ${C.fmono}`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = p.kind === "ring" ? col : this.onFill(col);
        ctx.fillText(p.text, sx, sy + 0.5);
        ctx.textAlign = "left";
      }
      occ.push([sx - r - 2, sy - r - 2, sx + r + 2, sy + r + 2]);
      if (p.id != null) this.hits.push({ id: p.id, sx, sy, r: r + 8 });
    }

    // line labels (the verdict distance) go on top of the pins, beside the line, clear of both ends
    ctx.font = `600 12px ${C.fmono}`;
    for (const { label, a, b } of lineLabels) {
      const w = ctx.measureText(label).width + 4;
      const spot = placeLineLabel(a, b, w, 16, occ, bounds);
      ctx.textBaseline = "middle";
      ctx.textAlign = "center";
      this.haloText(label, spot.x, spot.y, C.ink, 4);
      ctx.textAlign = "left";
      occ.push(spot.box);
    }

    // pin labels: each next to its own (possibly displaced) pin; forced first, then higher z / highlighted,
    // then numbered pins; less important labels give way or are dropped
    ctx.textBaseline = "middle";
    const rank = (p: Pin) => (p.force ? 1000 : 0) + (p.z ?? 0) * 10 + (p.halo ? 5 : 0) + (p.text ? 1 : 0);
    // a pin off the canvas gets no label (it would be pulled in, far from anything)
    const lab = marks.filter((m) => m.p.label && m.sx >= 0 && m.sx <= W && m.sy >= 0 && m.sy <= H);
    const spots = placeLabels(
      lab.map(({ p, sx, sy, r, x0, y0, moved }) => {
        ctx.font = `600 12.5px ${C.fsans}`;
        const w1 = ctx.measureText(p.label!).width;
        ctx.font = `11px ${C.fmono}`;
        const w2 = p.sub ? ctx.measureText(p.sub).width : 0;
        const d = Math.hypot(sx - x0, sy - y0);
        const dir = moved && d > 0.5 ? { x: (sx - x0) / d, y: (sy - y0) / d } : null;
        return { x: sx, y: sy, r, dir, w: Math.max(w1, w2), h: p.sub ? 30 : 16, rank: rank(p), force: !!p.force };
      }),
      occ,
      bounds,
      soft,
    );
    // leader lines first, under every label: a label pushed away from a crowded pin keeps a thread back to it
    ctx.save();
    ctx.lineCap = "round";
    lab.forEach(({ sx, sy, r }, i) => {
      const spot = spots[i];
      if (!spot) return;
      const text: Box = [spot.box[0] + 1, spot.box[1] + 1, spot.box[2] - 1, spot.box[3] - 1];
      const d = distToBox(sx, sy, text);
      if (d <= r + LEADER_GAP) return;
      const [nx, ny] = nearestOnBox(sx, sy, text);
      const ux = (nx - sx) / d;
      const uy = (ny - sy) / d;
      ctx.beginPath();
      ctx.moveTo(sx + ux * (r + 1), sy + uy * (r + 1));
      ctx.lineTo(nx - ux * 2, ny - uy * 2);
      ctx.strokeStyle = C.halo;
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.strokeStyle = C["ink-2"];
      ctx.lineWidth = 1;
      ctx.stroke();
    });
    ctx.restore();
    lab.forEach(({ p }, i) => {
      const spot = spots[i];
      if (!spot) return;
      occ.push(spot.box);
      ctx.font = `600 12.5px ${C.fsans}`;
      this.haloText(p.label!, spot.x, p.sub ? spot.y - 7 : spot.y, C.ink);
      if (p.sub) {
        ctx.font = `11px ${C.fmono}`;
        this.haloText(p.sub, spot.x, spot.y + 8, C["ink-2"]);
      }
    });
  }

  private drawCities(offs: number[], wpx: number, occ: Box[]): void {
    if (!this.places.length) return;
    const { ctx, C, t, W, H } = this;
    const overlaps = (b: Box) => occ.some((o) => b[0] < o[2] && b[2] > o[0] && b[1] < o[3] && b[3] > o[1]);
    const maxSr = wpx < 1500 ? 0 : wpx < 3000 ? 1 : wpx < 6000 ? 2 : wpx < 12000 ? 3 : wpx < 25000 ? 4 : wpx < 60000 ? 5 : wpx < 150000 ? 6 : wpx < 400000 ? 7 : 8;
    ctx.textBaseline = "middle";
    let n = 0;
    for (const p of this.places) {
      if (p.sr > maxSr) break;
      for (const o of offs) {
        const sx = t.x + (p.x + o * WU) * t.k;
        const sy = t.y + p.y * t.k;
        if (sx < 2 || sx > W - 2 || sy < 8 || sy > H - 8) continue;
        ctx.font = `${p.sr <= 2 || p.cap ? "500 12px " : "11px "}${C.fsans}`;
        const w = ctx.measureText(p.name).width;
        // name to the right of the dot, or to the left when it would run off the canvas or hit something
        const right: Box = [sx - 3, sy - 7, sx + 7 + w, sy + 7];
        const left: Box = [sx - 7 - w, sy - 7, sx + 3, sy + 7];
        const fits = (b: Box) => b[0] >= 2 && b[2] <= W - 2 && !overlaps(b);
        const side = fits(right) ? 1 : fits(left) ? -1 : 0;
        if (!side) continue;
        occ.push(side > 0 ? right : left);
        ctx.beginPath();
        ctx.arc(sx, sy, p.cap ? 2.8 : 2, 0, 7);
        ctx.fillStyle = C["city-dot"];
        ctx.fill();
        this.haloText(p.name, side > 0 ? sx + 5 : sx - 5 - w, sy, C.city);
        if (++n > 120) return;
      }
    }
  }

  private hitAt(x: number, y: number) {
    let best: (typeof this.hits)[number] | null = null;
    let bd = Infinity;
    for (const hh of this.hits) {
      const d = Math.hypot(hh.sx - x, hh.sy - y);
      if (d < hh.r && d < bd) {
        bd = d;
        best = hh;
      }
    }
    return best;
  }
}
